import axios from "axios";
import swal from "sweetalert";
import BASE_URL from "../assets/global/url";

const normalize = (value) =>
  String(value ?? "")
    .trim()
    .toUpperCase();

const describe = (item) =>
  `${item.check_number} — ${item.module}${
    item.transaction_number && item.transaction_number !== "--"
      ? ` (${item.transaction_number})`
      : ""
  }`;

/**
 * Looks up check numbers before they are added/saved.
 * A check number is rejected when it is already used anywhere in the system
 * (collections, payables, expenses, transfers, fixed assets, profit
 * distribution) or appears more than once in the current form.
 *
 * @param {string|string[]} checkNumbers - the new check number(s)
 * @param {object} [options]
 * @param {string[]} [options.pending] - check numbers already in the current
 *   form/table but not yet saved, so the same one cannot be entered twice
 * @param {string[]} [options.ignore] - check numbers already saved on this
 *   same record; they are not looked up again
 * @returns {Promise<{ type: "duplicate"|"exists", message: string }|null>}
 *   null when every check number is available
 * @throws when the lookup request fails
 */
export const getCheckNumberError = async (
  checkNumbers,
  { pending = [], ignore = [] } = {},
) => {
  const list = (Array.isArray(checkNumbers) ? checkNumbers : [checkNumbers])
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);
  if (list.length === 0) return null;

  // Duplicates inside the form itself
  const seen = new Set(pending.map(normalize).filter(Boolean));
  for (const value of list) {
    const key = normalize(value);
    if (seen.has(key)) {
      return {
        type: "duplicate",
        message: `Check number "${value}" is already entered in this transaction.`,
      };
    }
    seen.add(key);
  }

  const ignored = new Set(ignore.map(normalize));
  const toLookup = list.filter((value) => !ignored.has(normalize(value)));
  if (toLookup.length === 0) return null;

  const res = await axios.post(`${BASE_URL}/checkNumber/validate`, {
    checkNumbers: toLookup,
  });
  const existing = res.data?.existing || [];
  if (existing.length === 0) return null;

  return {
    type: "exists",
    message: `Check number already exists: ${existing.map(describe).join(", ")}`,
  };
};

/**
 * Same as getCheckNumberError, but shows the error in an alert.
 * @returns {Promise<boolean>} true when every check number is available
 */
export const validateCheckNumbers = async (checkNumbers, options) => {
  try {
    const error = await getCheckNumberError(checkNumbers, options);
    if (!error) return true;
    swal({
      title:
        error.type === "duplicate"
          ? "Duplicate Check Number"
          : "Check Number Already Exists",
      text: error.message,
      icon: "error",
    });
    return false;
  } catch (error) {
    console.error(error);
    swal({
      title: "Validation Failed",
      text: "Unable to validate the check number. Please try again.",
      icon: "error",
    });
    return false;
  }
};

/**
 * Shows the backend's check number guard response (409 CHECK_NUMBER_EXISTS),
 * raised when another user saved the same check number first.
 * Deferred so it replaces the page's own generic error alert.
 */
export const installCheckNumberErrorInterceptor = () =>
  axios.interceptors.response.use(undefined, (error) => {
    const data = error.response?.data;
    if (error.response?.status === 409 && data?.code === "CHECK_NUMBER_EXISTS") {
      setTimeout(() =>
        swal({ title: data.title, text: data.message, icon: "error" }),
      );
    }
    return Promise.reject(error);
  });
